import { useState, useEffect, useCallback } from 'react';

import { useEnvironment } from '~/src/contexts/environment';

import { getMinifiedSiteKey, asyncScriptLoad, isYandexCaptcha } from './Recaptcha.helpers';
import { RECAPTCHA_SCRIPT_URL, RECAPTCHA_DEV_TOKEN } from './Recaptcha.constants';
import { useYandexCaptcha } from './YandexCaptcha.hook';
import { RecaptchaAction, RecaptchaExposedApi } from './Recaptcha.types';

// v3 scores in the background and never shows a challenge: no widget to render,
// nothing to wait for, so the form is not gated on the captcha being ready.
function useGoogleRecaptcha(productName: string, isEnabled: boolean): RecaptchaExposedApi {
  const [recaptchaErrorCode, setRecaptchaErrorCode] = useState(''); // RIEXXX - Recaptcha Internal Error

  const siteKey = getMinifiedSiteKey(productName);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    asyncScriptLoad(`${RECAPTCHA_SCRIPT_URL}${siteKey}`)
      .catch(() => {
        setRecaptchaErrorCode('RIE001');
      });
  }, [isEnabled, siteKey]);

  // Minted per submit: the token is one-time and expires in two minutes, so
  // fetching it up front would hand the backend an expired one.
  const getRecaptchaToken = useCallback(async (action: RecaptchaAction): Promise<string> => {
    const { grecaptcha } = window as any;

    if (!grecaptcha?.ready) {
      setRecaptchaErrorCode('RIE002');
      throw new Error('reCAPTCHA is not loaded');
    }

    return new Promise<string>((resolve, reject) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(siteKey, { action }).then(resolve, reject);
      });
    });
  }, [siteKey]);

  return {
    recaptchaErrorCode,
    getRecaptchaToken,
  };
}

export function useRecaptcha(productName: string): RecaptchaExposedApi {
  const { isDevEnvironment } = useEnvironment();
  const isYandex = isYandexCaptcha(productName);

  const google = useGoogleRecaptcha(productName, !isDevEnvironment && !isYandex);
  const yandex = useYandexCaptcha(!isDevEnvironment && isYandex);

  // Dev stands disable the captcha on the backend too (recaptcha_disabled),
  // so the constant is never verified against a live key.
  const getDevToken = useCallback(async (): Promise<string> => RECAPTCHA_DEV_TOKEN, []);

  if (isDevEnvironment) {
    return { recaptchaErrorCode: '', getRecaptchaToken: getDevToken };
  }

  return isYandex ? yandex : google;
}
